<template>
  <div class="relative h-screen flex flex-col">
    <div v-if="shouldShowLoading" class="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <VaTypography tag="p" class="text-center">
        {{ t('common.loading') }}
      </VaTypography>
    </div>

    <RouterView class="flex-1 min-h-0" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ToastService } from './services/toast-service'
import { ToastOptions, useColors, useToast } from 'vuestic-ui'
import { useRoute } from 'vue-router'
import { onMounted, watch, computed } from 'vue'
import { useMsal } from './azure/useMsal'
import { useRouter } from 'vue-router'
import { useUserStore } from './stores/user-store'
import { useGlobalStore } from './stores/global-store'
import { storeToRefs } from 'pinia'
import { useDynamicFavicon } from './composables/useDynamicFavicon'
import { useDynamicTitle } from './composables/useDynamicTitle'
import { useDynamicTheme } from './composables/useDynamicTheme'
import { useAutoClientNavigation } from './composables/useAutoClientNavigation'
import { useCurrentUserProfile } from './composables/useCurrentUserProfile'
import { useAppLoadingStore } from '@/stores/app-loading-store'

const { t, locale } = useI18n()
const { init, close, closeAll } = useToast()
const router = useRouter()
const route = useRoute()

// Check if we're in development mode
const isDev = computed(() => import.meta.env.DEV)

const checkAndDisplayToast = () => {
  // load toasts stored in localStorage
  const toasts = ToastService.loadToastMessagesJson()

  // show toasts
  toasts?.forEach((toast: ToastOptions) => {
    init(toast)
  })
}

// Watch for route changes
watch(() => route.path, checkAndDisplayToast)

const userStore = useUserStore()

const { accounts, isAuthenticated } = useMsal()

// Initialize auto client navigation
useAutoClientNavigation()

// Initialize dynamic favicon, title and theme
const { updateFavicon } = useDynamicFavicon()
const { updateTitle } = useDynamicTitle()
const { updateTheme } = useDynamicTheme()

// Initialize current user profile loading (includes dark mode initialization)
const { initializeUserProfile } = useCurrentUserProfile()
initializeUserProfile()

const appLoadingStore = useAppLoadingStore()

const shouldShowLoading = computed(() => {
  return appLoadingStore.isLoading
})

// Debug: Log when dynamic theme is initialized
console.log('Dynamic theme composable initialized')

// watch for isAuthenticated changes
// if isAuthenticated is false, redirect to login page
watch(isAuthenticated, (isAuthenticated) => {
  console.log('isAuthenticated', isAuthenticated)

  if (!isAuthenticated) {
    // Avoid clobbering the branded login redirect context.
    const isAlreadyOnAuthRoute = route.matched.some((record) => (record.path || '').startsWith('/auth'))
    if (isAlreadyOnAuthRoute) {
      return
    }

    const anyRouteRecordIsCheckClients = route.matched.some((record) => record.name === 'check-clients')
    const clientSegment = anyRouteRecordIsCheckClients
      ? (route.params.clientCode as string) || route.path.split('/')[1]
      : undefined
    const redirectStartPage = route.fullPath

    try {
      sessionStorage.setItem('redirectStartPage', redirectStartPage)
      if (clientSegment) {
        sessionStorage.setItem('clientSegment', clientSegment)
      } else {
        sessionStorage.removeItem('clientSegment')
      }
    } catch {
      // ignore
    }

    router.push({
      name: 'login',
      query: {
        ...(clientSegment ? { clientSegment } : {}),
        redirectStartPage,
      },
    })
  }
})

// updateStoreFromAccounts
const updateStoreFromAccounts = (accounts: any) => {
  console.log('updateStoreFromAccounts accounts:', [...accounts])

  const activeAccount = accounts[0]
  // set account info into store8888
  userStore.changeAccountInfo({
    userName: activeAccount?.name ?? 'N/A',
    email: activeAccount?.username ?? 'N/A',
  })
}

// watch for accounts changes
watch(accounts, (newAccounts) => {
  // Defensive: newAccounts may be a ref or a value
  const arr = Array.isArray(newAccounts) ? newAccounts : []
  if (arr && arr.length > 0) {
    updateStoreFromAccounts(arr)
  }
})

onMounted(() => {
  const savedLocale = window.localStorage.getItem('locale')
  if (savedLocale) {
    locale.value = savedLocale
  }

  // Note: Dark mode is now managed per-user via useUserDarkMode composable
  // Global dark mode setting is no longer used
  // const { settings } = storeToRefs(useGlobalStore())
  // const { applyPreset, currentPresetName } = useColors()

  // Default to light mode on initial load - user dark mode will override if needed
  const { applyPreset } = useColors()
  applyPreset('light')

  // check and display toasts
  checkAndDisplayToast()

  // update store from accounts
  const arr = Array.isArray(accounts) ? accounts : accounts.value
  if (arr && arr.length > 0) {
    updateStoreFromAccounts(arr)
  }
})
</script>
<style lang="scss">
@import 'scss/main.scss';

#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  margin: 0;
  min-width: 20rem;
}
</style>
